import { useEffect, useRef } from "react";
import type { ArtInfo } from "../api/types";

const POLL_INTERVAL_MS = 10_000;
const TEN_MINUTES_MS = 10 * 60 * 1000;

/**
 * Polls `refetch` (which should invalidate `/info`) every 10 s while the
 * wayback capture is pending and still within the 10-minute window.
 * Extracted from `startWaybackPolling` + the dependent `useEffect` in
 * the monolith (lines 1105–1165).
 *
 * `enabled` should be `true` only when generation is `completed` and
 * `art.generationStatus === "completed"`; otherwise this hook is a no-op.
 */
export function useWaybackPolling(opts: {
  enabled: boolean;
  art: ArtInfo | null | undefined;
  refetch: () => void;
}): void {
  const { enabled, art, refetch } = opts;
  const artRef = useRef(art);

  useEffect(() => {
    artRef.current = art;
  }, [art]);

  useEffect(() => {
    if (!enabled) return;
    // tiny delay so the initial /info fetch can settle (parity with monolith).
    const startTimer = setTimeout(start, 2000);
    let intervalId: ReturnType<typeof setInterval> | null = null;

    function shouldPoll(): boolean {
      const current = artRef.current;
      if (!current) return false;
      if (current.waybackMachineUrl) return false;
      if (current.waybackMachineStatus === "failed") return false;
      if (current.waybackMachineStatus === "pending") return true;
      if (current.waybackMachineRequestedAt) {
        const requestedAt = new Date(current.waybackMachineRequestedAt).getTime();
        return Date.now() - requestedAt < TEN_MINUTES_MS;
      }
      return false;
    }

    function start(): void {
      if (!shouldPoll()) return;
      intervalId = setInterval(() => {
        if (!shouldPoll()) {
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
          return;
        }
        refetch();
      }, POLL_INTERVAL_MS);
    }

    return () => {
      clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled, refetch]);
}
